import React, { createContext, useState, useEffect, useContext } from 'react'
import { getProducts } from '../api/products'

const ProductContext = createContext()

export const useProducts = () => {
  const context = useContext(ProductContext)
  if (!context) {
    throw new Error('useProducts must be used within a ProductProvider')
  }
  return context
}

export const ProductProvider = ({ children }) => {
  const [products, setProducts] = useState([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState(null)

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        setIsLoading(true)
        const data = await getProducts()
        setProducts(data)
        setError(null)
      } catch (err) {
        setError('Failed to load products')
        console.error('Error fetching products:', err)
      } finally {
        setIsLoading(false)
      }
    }

    fetchProducts()
  }, [])

  // Helper function to get product by ID (maintains compatibility)
  const getProductData = (id) => {
    const product = products.find((product) => product.id === id || product._id === id)
    if (!product) return null

    // Add helper method to get the current price (specialPrice if available, otherwise price)
    return {
      ...product,
      getCurrentPrice: () => {
        return product.specialPrice ? product.specialPrice : product.price
      },
    }
  }

  // Helper function to get product by MongoDB ObjectId
  const getProductByObjectId = (objectId) => {
    return products.find(
      (product) => product._id === objectId || (product._id && product._id.$oid === objectId)
    )
  }

  // Helper function to get the current price for any product
  const getCurrentPrice = (product) => {
    if (!product) return null
    return product.specialPrice ? product.specialPrice : product.price
  }

  const value = {
    products,
    isLoading,
    error,
    getProductData,
    getProductByObjectId,
    getCurrentPrice,
    refetch: () => {
      const fetchProducts = async () => {
        try {
          setIsLoading(true)
          const data = await getProducts()
          setProducts(data)
          setError(null)
        } catch (err) {
          setError('Failed to load products')
          console.error('Error fetching products:', err)
        } finally {
          setIsLoading(false)
        }
      }
      fetchProducts()
    },
  }

  return <ProductContext.Provider value={value}>{children}</ProductContext.Provider>
}

export { ProductContext }
